import { useCallback, useEffect, useRef, useState } from "react";

import useEmailParam from "@/v1/components/hooks/useEmailParam";
import type { UserFilterOptions } from "@/v1/components/pages/Mandanten/Common/UserFilters";
import type { IUser } from "@/v1/types";
import { destroyMessage, showLoadingMessage } from "@/v1/utils";
import api from "@/v1/api";
import { useCustomerSearch } from "@/lib/customerSearch/useCustomerSearch";

const LOADING_MESSAGE_KEY = "finding-user";

export const useFilteredUser = () => {
  const [emailParam, setEmailParam] = useEmailParam();
  const [filterOptions, setFilterOptions] = useState<UserFilterOptions>({
    search: emailParam || "",
    all: false,
  });
  const [user, setUser] = useState<IUser | undefined>();
  // Only the details of the newest selection may land: an older request that
  // answers late must not show a customer the URL no longer selects.
  const lastUserRequestId = useRef(0);

  const fetchUser = useCallback(async (email: string) => {
    const requestId = ++lastUserRequestId.current;
    const isStale = () => requestId !== lastUserRequestId.current;
    if (!email) {
      return;
    }
    showLoadingMessage(LOADING_MESSAGE_KEY);
    try {
      const user = await api.kontax.getUserByEmail(email);
      if (!isStale()) {
        setUser(user);
      }
    } catch (error) {
      if (!isStale()) {
        setUser(undefined);
      }
    } finally {
      if (!isStale()) {
        destroyMessage(LOADING_MESSAGE_KEY);
      }
    }
  }, []);

  useEffect(() => {
    if (emailParam) {
      fetchUser(emailParam);
    } else {
      // Clearing the selection also discards any details still on the way.
      lastUserRequestId.current++;
      destroyMessage(LOADING_MESSAGE_KEY);
      setUser(undefined);
    }
  }, [emailParam, fetchUser]);

  // Only the resolved customer's email goes into `email`, never the typed
  // text: the left navigation carries the param to views whose
  // `/users/:email` calls cannot use a Public ID (SMT-1396).
  const selectEmail = useCallback(
    (email: string | undefined) => setEmailParam(email),
    [setEmailParam]
  );
  const { search } = useCustomerSearch(selectEmail);

  useEffect(() => {
    search(filterOptions.search);
  }, [filterOptions, search]);

  return {
    user,
    filterOptions,
    setFilterOptions,
  };
};
